import { store } from '../root/ReduxStore.store';
import type {
  Facets,
  PricingStrategy,
  ProductSegment,
  ProductVariantCategory,
} from '../product/product-model';

export interface OrderDetailsRequest {
  workspaceId: string;
  orderId: number;
  body: OrderDetailsRequestBody;
}

interface ShippingLines {
  listPrice: number;
  listPriceIncludesTax: boolean;
  adjustments: never[];
  taxLines: {
    description: string;
    taxRate: number;
  }[];
  orderId: number;
  id: number;
  shippingMethodId: number;
  price: number;
  priceWithTax: number;
  discountedPrice: number;
  discountedPriceWithTax: number;
  taxRate: number;
  discounts: any[];
}

export interface OrderDetailsRequestBody {
  includeInvoice: boolean;
  includeTax: boolean;
  includeCustomer: boolean;
  includePromotions: boolean;
  customerId: string;
  includePromotionData: boolean;
}

// testing vitetst

export interface OrderDetailsResponse {
  paymentTerm?: string;
  deliveryTerms?: string;
  customerName?: string;
  notifyToBuyer?: boolean;
  id: number;
  documentDate: string;
  customerId: string;
  divisionId: string;
  headDivisionId: string;
  status: string;
  importSource: string;
  createdBy: string;
  createdByName: string;
  userType: string;
  distributorName: string;
  distributorFirmName: string;
  subTotal: string;
  subTotalWithTax: string;
  taxAmount: string;
  total: string;
  totalWithTax: string;
  fullFillmentLocationId: string;
  cfaName: string;
  refOrderNumber: string;
  splitBy: string;
  createdAt: string;
  updatedAt: string;
  skucount: number;
  totalQuantity: number;
  discount: string;
  divisions: Division[];
  lines: OrderLine[];
  poFile: PoFile;
  estimatedDeliveryDate: null;
  showPrice: boolean;
  invoiceSummary: OrderInvoiceSummary;
  notes?: CheckoutNotes;
  shippingLines?: ShippingLines[];
  shippingAddress?: ShippingAddress;
  billingAddress?: ShippingAddress;
  includePaymentLink?: boolean;
  priceListId?: string;
  poNumber?: string;
  orderDiscount?: {
    orderId: any;
    discount: number;
    discountType: any;
  };
  tags: any[];
  advancePayment?: string | null;
  advancePaymentAmount?: string;
  advancePaymentType?: string | null;
}

export interface Division {
  divisionId: string;
  divisionName: string;
}

export interface OrderInvoiceSummary {
  totalQty: number;
  totalAmount: string;
  invoicedItem?: number;
  invoiceCount?: number;
  invoices: any[];
  discount?: string;
  taxAmount?: string;
  totalItems?: number;
}

export type DiscountType = 'percent' | 'direct';

export interface LineDiscount {
  discount: string;
  discountType: DiscountType;
}

export interface OrderLine {
  discountType?: DiscountType;
  discount?: number;
  productVariantId?: number;
  poFileLineId?: string;
  packSize?: number;
  uploadedProductName?: string;
  uploadedQty?: number;
  sequence?: string | number;
  createdAt?: string;
  updatedAt?: string;
  initialListPrice?: string;
  listPrice?: any;
  listPriceIncludesTax?: boolean;
  quantity?: number;
  cancelledQuantity?: number;
  minOrderQty?: number;
  erpOrderNumber?: string;
  orderId?: number;
  unitPrice?: string;
  unitPriceWithTax?: string;
  discountedUnitPrice?: string;
  discountedUnitPriceWithTax?: string;
  proratedUnitPrice?: string;
  proratedUnitPriceWithTax?: string;
  linePrice?: string;
  linePriceWithTax?: string;
  discountedLinePrice?: string;
  discountedLinePriceWithTax?: string;
  lineTax?: string;
  divisionId?: string;
  divisionName?: string;
  headDivisionId?: string;
  headDivisonName?: string;
  proratedLinePrice?: string;
  proratedLinePriceWithTax?: string;
  proratedLineTax?: string;
  isDeleted?: boolean;
  id?: number;
  refundId?: null;
  customFields?: CustomFields;
  productVariant: OrderProduct;
  unitPriceWithTaxChangeSinceAdded?: number;
  totalLi?: number;
  discounts?: any[];
  taxLines?: TaxLine[];
  promotionIds?: number[];
  promotionNames?: string[];
  adjustedPrice?: string;
  invoiceLineSummary?: InvoiceLineSummary;
  lineDiscount?: LineDiscount;
  newQnty?: number;
  newPrice?: number;
  status?: string;
  poFileId?: string;
  additionalUOM?: string;
  baseUOM?: string;
}

export interface CustomFields {
  perItemPrice: null;
  skuType: null;
}

export interface OrderProduct {
  MOQ: number;
  id: number;
  sku: string;
  translations: Translation[];
  packSize: number;
  productVariantId: number;
  assetUrl: string;
  /** When 0, line should be treated as out of stock in UI. */
  stockOnHand?: number;
  additionalUOM?: string;
  additionalUom?: string;
  additionaluom?: string;
  pricingStrategy?: PricingStrategy;
  baseUOM?: string;
  baseUom?: string;
  baseuom?: string;
  facetValues: {
    id: number;
    code: string;
    name: string;
    facet: {
      id: number;
      code: string;
      name: string;
    };
  }[];
}

export interface TaxLine {
  taxRate: number;
  taxAmount: string;
  taxCategory: string;
  taxCode: string;
}

export interface PoProduct {
  sku: string | number;
  MOQ?: number;
  description?: string;
  name: string;
  packSize?: number;
  productVariantId?: number;
  dtProductName?: string;
  additionalUOM?: string;
  additionalUom?: string;
  additionaluom?: string;
  baseUOM?: string;
  baseUom?: string;
  baseuom?: string;
  pricingStrategy?: PricingStrategy;
}

export interface POLineData {
  id: string;
  divisionName?: string;
  proratedLinePrice: number;
  proratedLinePriceWithTax: number;
  status: string;
  orderLineId?: number;
  productVariantId: number;
  linePrice: number;
  linePriceWithTax: number;
  discountedLinePrice: number;
  discountedLinePriceWithTax: number;
  unitPrice: number;
  listPrice: number;
  CFA: string;
  quantity: number;
  divisionId: string;
  orderId: number;
  product?: PoProduct;
  promotionId: number;
  headDivisionId?: string;
  headDivisionName?: string;
  refOrderNumber: string;
  CFAId: string;
  modeOfOrder: string;
  schemeType: string;
  discountedUnitPrice: number;
  uploadedQty?: number;
  taxCategory?: string;
  errorMessage?: string;
  poFileId?: string;
  lineDiscount: LineDiscount;
  promotions: any[];
  assetUrl: string;
  freeQuantity: number;
  promotionName: string;
  facets?: {
    facetName: string;
    facetValue: string;
    facetValueCode: string;
  }[];
  sequence: number;
  MOQ: number;
  uploadedProductName: string | null;
  distributorProductName: string | null;
  priceListId?: string | null;
  priceListName?: string;
  priceListCode?: string;
  notes?: string | null;
  communicationChannelId?: string | null;
  billingAddress?: unknown | null;
  shippingAddress?: unknown | null;
}

export interface PoFile {
  id: string;
  poNumber: null;
  importSource: string;
  number: string;
  uploadedDate: string;
  skuCount: number;
  totalItemsCount: number;
  subTotal: string;
  subTotalWithTax: string;
  customerName?: string;
  taxAmount: string;
  discount: string;
  total: string;
  totalWithTax: number;
  proratedPrice: string;
  proratedPriceWithTax: number;
  status: string;
  lines?: POLineData[];
  orderState?: string;
  createdBy?: string;
  customerId: string;
  isMapped?: boolean;
  freshdeskTicketId?: number | string | null;
  mappedLineCount?: number | string;
}

export interface CustomFields {
  perItemPrice: null;
  skuType: null;
}

export interface PoOrderDetailsResponse {
  divisions: Division[];
  lines: POLine[];
  allStatus: AllStatus;
  pofileId: string;
  status: string;
  orderState: string;
  refOrderNumber: string;
  customerId: string;
  skuCount: string;
  uploadedDate: string;
  importSource: string;
  poNumber: string;
  totalQuantity: number;
  waMessage: WaMessage;
  total: string;
  totalWithTax: string;
  tax: string;
  subTotal: string;
  subTotalWithTax: string;
  discount: string;
  userType: string;
  userName: string;
  orderedBy: string;
  cfa: null;
  invoiceSummary: OrderInvoiceSummary;
  showPrice: boolean;
  notifyToBuyer: boolean;
  orderDiscount: {
    orderId: any;
    discount: number;
    discountType: any;
  };
  customerName: string;
  customerFirmName: string;
  tags: any[];
  notes?: CheckoutNotes;
  shippingLines: ShippingLines[];
  shippingAddress?: ShippingAddress;
  billingAddress?: ShippingAddress;
  includePaymentLink?: boolean;
  priceListId?: string;
  priceListName?: string;
  deliveryTerms?: string;
  advancePayment?: string | null;
  advancePaymentAmount?: string;
  advancePaymentType?: string | null;
}

export interface AllStatus {
  MODIFYING: string;
  CANCELLED: string;
  CONFIRMED: string;
  BILLED: string;
  PAYMENT_AUTHORIZED: string;
  WAITING_FOR_CONFIRMATION: string;
  APPROVED_BY_CFA: string;
  SUBMITTED: string;
  SUBMITTED_BY_CFA: string;
  DISPATCHED: string;
  REJECTED_BY_CFA: string;
  DELIVERED: string;
  ACKNOWLEDGED_BY_CFA: string;
  DRAFT: string;
  IN_CART: string;
  MAPPING_FAILED: string;
  PARSING_FAILED: string;
}

export interface POLine {
  poFileLineId?: string;
  errorMessage?: string;
  status?: string;
  createdAt?: string;
  initialListPrice?: string;
  uploadedProductName: string;
  distributorProductName: string;
  uploadedQty: number;
  listPrice?: string;
  quantity?: number;
  sequence: number;
  erpOrderNumber?: string;
  refOrderNumberL?: string;
  orderId?: number;
  productVariantId: number;
  unitPrice: string;
  unitPriceWithTax?: string;
  discountedUnitPrice?: string;
  discountedUnitPriceWithTax?: string;
  proratedUnitPrice?: string;
  proratedUnitPriceWithTax?: string;
  linePrice: string;
  linePriceWithTax?: string;
  discountedLinePrice?: string;
  discountedLinePriceWithTax?: string;
  lineTax?: string;
  cfaId?: string;
  cfa?: string;
  divisionId?: string;
  divisionName?: string;
  headDivisionId?: string;
  headDivisionName?: string;
  proratedLinePrice?: string;
  proratedLinePriceWithTax?: string;
  proratedLineTax?: string;
  orderLineId?: number;
  productVariant: OrderProduct;
  MOQ: number;
  packSize: number;
  unitPriceWithTaxChangeSinceAdded?: number;
  totalLi: number;
  discounts: any[];
  taxLines: TaxLine[];
  promotionIds: number[];
  promotionId: number;
  promotionNames: string[];
  adjustedPrice?: string;
  invoiceLineSummary?: InvoiceLineSummary;
  lineDiscount?: LineDiscount;
  newQnty?: number;
  newPrice?: number;
  baseUom?: UOM;
  additionalUom?: UOM;
  poFileId?: string;
}

export interface InvoiceLineSummary {
  totalQty: number;
  totalAmount: string;
  invoicedItem: number;
  invoiceLines: any[];
}

export interface Translation {
  name: string;
}

export interface WaMessage {
  type: string;
  data: number[];
}

export interface TrackPODetailsRequest {
  workspaceId: string;
  body: {
    pofileId: string;
    includeInvoice?: boolean;
    includePromotion?: boolean;
    includePromotionData: boolean;
    includeTax?: boolean;
    includeInCartItemsOnly?: boolean;
    filter?: {
      divisionIds?: string[];
      status?: string[];
      invoice?: string;
      scheme?: string;
    };
    searchKey?: string;
    sortBy?: string;
    sortDirection?: string;
    customerId?: string;
  };
  dispatchFilters?: boolean;
  orderId?: string | number;
}

export interface OrderLogsRequest {
  workspaceId: string;
  orderId: number;
  action: string;
}

export interface Audit {
  date: string;
  time: string;
  updatedFields: UpdatedFields;
  timestamp: string;
  action: OrderLogsTypes;
  productName: string;
  oldProductName?: string;
  oldSku: string;
  sequence: number;
  sku: string;
  updatedBy: string;
}

interface UpdatedFields {
  quantity: number;
  oldQuantity: number;
  productDetails?: ProductDetails;
  oldProductDetails?: ProductDetails;
}

interface ProductDetails {
  name: string;
  sku: string;
}
export enum OrderLogsTypes {
  UPDATE = 'update',
  DELETE = 'delete',
  INSERT = 'insert',
  REMAPPED = 'remapped',
}

export interface OrderLogsResponse {
  orderId: number;
  orderLineId: number;
  activeQuantity: number;
  productDetails: ProductDetails;
  activeProductVariantId: number;
  sequence: number;
  Activequantity: number;
  sku: string;
  productVariantId: number;
  poFileLineId: string;
  data: OrderLogsData[];
}

export interface OrderLogsData {
  updatedFields: UpdatedFields;
  timestamp: string;
  action: string;
  updatedBy: string;
}

export interface AddItemToActiveOrderRequest {
  customerId: string;
  sellerWorkspaceId: string;
  source?: string;
  fullFillmentLocationId?: string;
  poFileId?: string;
  lines: Line[];
  campaignId?: string;
  attemptId?: string;
}

export interface Line {
  productVariantId: number;
  quantity: number;
  poFileLineId?: string;
  operator?: string;
}

export interface AddItemToActiveOrderResponse {
  orders: ActiveOrder[];
  status: string;
  statusCode: number;
  errors: any[];
}

export interface ActiveOrder {
  id: number;
  status: string;
  suggestedValue: string;
  suggestedValueWithTax: string;
  taxAmount: string;
  discount: string;
  inviteId?: string;
  fullFillmentLocationId: string;
  CFA: string;
  headDivisionName: null;
  headDivisionId: null;
  pofileId: string;
  importSource: string;
  orderLine: ActiveOrderLine[];
}

export interface ActiveOrderLine {
  id?: number;
  sku?: string;
  skuName?: string;
  divisionId?: string;
  description?: string;
  unitPrice?: string;
  productVariantId: number;
  quantity?: number;
  linePrice?: string;
  linePriceWithTax?: string;
  discountedLinePrice?: string;
  discountedLinePriceWithTax?: string;
  proratedLinePrice?: string;
  proratedLinePriceWithTax?: string;
  suggestedValue?: string;
  suggestedQuantity?: number;
  changedValue?: string;
  adjustments?: any[];
  minOrderQty?: number;
  listPrice?: number;
  orderId?: number;
  productVariant?: {
    additionalUOM?: string;
    baseUom?: string;
    packSize?: number;
  };
  promotionId?: number;
  promotionName?: string;
  categories?: ProductVariantCategory[];
  segments?: ProductSegment[];
  facets?: Facets[];
}

export interface DeletePoFileRequest {
  workspaceId: string;
  poFileLineIds?: string[];
  poFileId: string;
  customerId: string;
  poFileImportSource?: string;
}

export interface DeletePoFileResponse {
  success: boolean;
  message: string;
}

export interface OrderCheckoutRequest {
  customerId: string;
  sellerWorkspaceId: string;
  poFileIds?: string[];
  shippingAddress?: ShippingAddress;
  billingAddress?: ShippingAddress;
  notes?: CheckoutNotes;
}

export interface CheckoutOrder {
  id: number;
  status: string;
  suggestedValue: string;
  suggestedValueWithTax: string;
  taxAmount: string;
  discount: string;
  fullFillmentLocationId: string;
  orderLine: ChekoutOrderLine[];
  refOrderNumber: string;
  inviteId: string;
  orderId: number;
  divisionId: any;
  headDivisionId: any;
  orderPlacedAt: string;
  orderValue: number;
  orderValueWithTax: number;
  importSource: string;
}
interface ChekoutOrderLine {
  productVariantId: number;
  quantity: number;
  productVariant: UpdatedProductVariant;
  cfaId: string;
  minOrderQty: number;
  divisionId: string;
  headDivisionId: string;
}

interface UpdatedProductVariant {
  createdAt: string;
  updatedAt: string;
  deletedAt: any;
  enabled: boolean;
  isDeleted: boolean;
  ZINS: boolean;
  sku: string;
  sortOrder: number;
  stockOnHand: number;
  stockAllocated: number;
  outOfStockThreshold: number;
  useGlobalOutOfStockThreshold: boolean;
  trackInventory: string;
  id: number;
  taxCategoryId: number;
  productId: number;
  productVariantPrices: ProductVariantPrice[];
  taxCategory: TaxCategory;
  divisionCFAProductVariants: DivisionCfaproductVariant[];
  translations: UpdatedTranslation[];
  price: number;
  priceWithTax: number;
  listPrice?: number;
  listPriceIncludesTax?: boolean;
  taxRateApplied?: UpdatedTaxRateApplied;
  currencyCode?: string;
}

interface ProductVariantPrice {
  createdAt: string;
  updatedAt: string;
  price: number;
  id: number;
  channelId: number;
}

interface TaxCategory {
  createdAt: string;
  updatedAt: string;
  name: string;
  code: string;
  isDefault: boolean;
  id: number;
}

interface DivisionCfaproductVariant {
  id: string;
  createdDate: string;
  updatedDate: string;
  divisionId: string;
  productVariantId: number;
  cfaId: string;
  isActive: boolean;
  minOrderQty: number;
  packSize: number;
}

interface UpdatedTranslation {
  name: string;
  shortDescription: string;
}

interface UpdatedTaxRateApplied {
  createdAt: string;
  updatedAt: string;
  name: string;
  enabled: boolean;
  value: number;
  id: number;
  categoryId: number;
  category: TaxCategory;
  zone: Zone;
  customerGroup: any;
}

interface Zone {
  createdAt: string;
  updatedAt: string;
  name: string;
  id: number;
}

export type ErrorItem = ErrorsRes | string;

export interface EditOrderResponse {
  message: string;
  orderLines: OrderLine[];
  errors: ErrorItem[];
  order: OrderDetailsResponse;
}

export interface ErrorsRes {
  msg: string;
}

export interface EditOrderRequest {
  workspaceId: string;
  orderId?: string | number;
  customerId?: string;
  poFileId?: string;
  lines?: any;
  body?: {
    orderId?: string | number;
    customerId: string;
    lines?: UpdatedLine[];
    promotionIds?: (string | number)[];
    poFileId?: string;
    discount?: number;
    advancePayment?: number | null;
    advancePaymentType?: string | null;
    discountType?: DiscountType;
    priceListId?: string;
  };
}

export interface UpdatedLine {
  productVariantId?: number;
  poFileLineId?: string;
  quantity: number;
  orderLineId?: number;
  operator?: string;
  unitPrice?: number;
  listPrice?: number | string;
  discount?: number;
  discountType?: DiscountType;
}

export interface SuggestedProducts {
  companyProductCode: string;
  productName: string;
  mdmProductCode: string;
  productVariantId: number;
  pts: number;
}

export interface SuggestMappingRequest {
  workspaceId: string;
  poFileLineId: string;
  customerId: string;
}

export interface ProductMappingRequest {
  inviteId: string;
  workspaceId: string;
  POLines: POLine[];
}

export interface ProductMappingResponse {
  records: MappingRecord[];
  statusCode: number;
  message: string;
}

export interface MappingRecord {
  distributorErpSku: string;
  principalErpSku: string;
  distributorProductName: string;
  productVariantId: number;
  unitQuantity: number;
  isMapped: boolean;
  mappedBy: string;
  manualMappingSync: boolean;
  manualMappingResponse: string;
  status: string;
  packing: string;
}

export interface PoListOrderRequest {
  customerId?: string;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
  includeActiveOrders?: boolean;
  includeInvoice?: boolean;
  includeInvoiceLines?: boolean;
  includeOrderInvoices?: boolean;
  includeSummary?: boolean;
  searchKey?: string;
  orderState?: string[];
  filterParams?:
    | {
        customerIds?: string[];
        divisionIds?: string[];
      }
    | string[];
  startDate?: string;
  endDate?: string;
  importSource?: 'manual' | 'upload' | 'whatsapp';
}

export interface PoListOrderResponse {
  paymentTerm?: string;
  poNumber?: string;
  deliveryTerms?: string;
  advancePayment?: string;
  advancePaymentAmount?: string;
  advancePaymentType?: string;
  shippingAddress?: PoOrderShipping;
  shippingLines: ShippingLines[];
  includePaymentLink?: boolean;
  notes?: {};
  files: PoFile[];
  summary: PoSummary;
  status: string;
  statusCode: number;
  poFileCount: number;
  totalRecords: number;
  startRecord: number;
  endRecord: number;
}

export interface PoOrderShipping {
  latitude: number | null;
  longitude: number | null;
  streetLine1: string;
}

export interface PoSummary {
  totalSKUCount: number;
  totalOrderValue: number;
  totalDiscount: number;
  totalTax: number;
  totalFreeItems?: number;
  grossOrderNumber?: number;
  grossOrderValue?: number;
  totalFreeQuantity?: number;
  totalItemsCount?: number;
  divisions?: { id: string; name: string }[];
  cfas?: { cfaName: string; fullFillmentLocationId: string }[];
  orderStates?: string;
}

export interface POFileDetailsByIdRequest {
  poFileId: string;
  workspaceId: string;
  params: {
    includePromotionData: boolean;
    sellerWorkspaceId: string;
    customerId: string;
    campaignType?: string;
  };
}

export interface POFileDetailsByIdResponse {
  id: string;
  number: any;
  uploadedDate: string;
  skuCount: number;
  totalItemsCount: number;
  importSource: string;
  freshdeskTicketId: any;
  totalWithTax: number;
  status: string;
  showPrice: boolean;
  subTotal: number;
  lines: {
    id: number;
    divisionName: string;
    proratedLinePrice: number;
    proratedLinePriceWithTax: number;
    status: string;
    orderLineId: number;
    productVariantId: number;
    assetUrl: string;
    linePrice: number;
    linePriceWithTax: number;
    discountedLinePrice: number;
    discountedLinePriceWithTax: number;
    unitPrice: number;
    discountedUnitPrice: number;
    CFA: string;
    quantity: number;
    orderId: number;
    freeQuantity: number;
    product: {
      sku: string;
      productVariantId: number;
      MOQ: number;
      description: string;
      name: string;
      packSize: number;
    };
    promotionId: number;
    CFAId: string;
    divisionId: string;
    modeOfOrder: string;
    taxCategory: string;
  }[];
  freeQuantity: number;
  notes?: CheckoutNotes;
  shippingAddress?: ShippingAddress;
  billingAddress?: ShippingAddress;
}

export interface GetCartRequest {
  workspaceId: string;
  params: {
    sellerWorkspaceId: string;
    customerId: string;
    includeActiveOrders?: boolean;
    includeInvoice?: boolean;
    includeInvoiceLines?: boolean;
    includeOrderInvoices?: boolean;
    includeSummary?: boolean;
    importSource?: 'manual' | 'upload' | 'whatsapp';
  };
}

export interface GetCartSummaryRequest {
  sellerWorkspaceId: string;
  customerId: string;
}

export interface GetCartSummaryResponse {
  totalFreeQuantity: number;
  totalSKUCount: number;
  grossOrderValue: number;
  totalDiscount: number;
  totalTax: number;
  subTotal: number;
  totalItemsCount: number;
  totalOrderValue: number;
  grossOrderNumber: number;
}

export interface DeleteLinesRequest {
  params: {
    sellerWorkspaceId: string;
  };
  body: {
    customerId: string;
    sellerWorkspaceId: string;
    lines: DeletedLine[];
    importSource?: string;
    poFileId?: string;
  };
}

export interface RemovedOrderPromotionRequest {
  workspaceId: string;
  orderId: number;
  promotionId: number;
}

export interface DeletedLine {
  orderId: number;
  orderLineId?: number;
}

export interface EmbeddedSignupCallbackRequest {
  event: string;
  esLinkID: string;
  data: {
    phone_number_id: string;
    waba_id: string;
  };
  sellerWorkspaceId: string;
}

export interface EmbeddedSignupCallbackResponse {
  message: string;
  requestId: number;
}

export interface ListOrderRequest {
  params: {
    workspaceId: string;
    customerWorkspaceId?: string;
    customerId?: string;
    startDate?: string;
    endDate?: string;
    searchKey?: string;
  };
  body: {
    workspaceId: string;
    customerId: string;
    pageNo?: number;
    pageSize?: number;
    sortBy: string;
    sortDirection: string;
    startDate?: string;
    endDate?: string;
    includeSummary?: boolean;
    orderLine?: any[];
    filterModel?: {
      divisionIds?: string[];
      headDivisionIds?: string[];
      cfaIds?: string[];
      status?: string[];
      customerIds?: string[];
      importSource?: string;
      tagIds?: any[];
      sourceFilter?: string;
      usertype?: string;
    };
    searchKeyword?: string;
    includeProductInfo?: boolean;
    includeCustomer?: boolean;
    includeInvoice?: boolean;
    includeOrderHistory?: boolean;
    includeOrderLineHistory?: boolean;
    includeCFA?: boolean;
    includeDivision?: boolean;
    includeStatus?: boolean;
    skip?: number | boolean;
  };
  dispatchFilters?: boolean;
}

export interface ListOrderResponse {
  order: OrderDataResponse[];
  totalRecords: number;
  startRecord: number;
  endRecord: number;
  totalOrderValue?: string;
  totalAmountSaved?: string;
  invoicedTotalAmount?: string;
  invoicedTotalQuantity?: string;
}

export interface OrderDataResponse {
  createdBy: string;
  createdByName: string | null;
  customerFirmName: string;
  customerId: string;
  customerName: string;
  discount: string;
  documentDate: string;
  erpOrderNumber: string | null;
  fullFillmentLocationId: string;
  id: number;
  invoice: OrderInvoice;
  invoiceLines: any[];
  invoicedItem: number;
  totalAmount: string;
  totalQty: number;
  invoiceSummary: OrderInvoiceSummary;
  invoiceCount: number;
  invoices: any[];
  isSpecial: boolean;
  orderMetaData: OrderMetaData;
  campaignId: string | null;
  cfa: string;
  divisionName: string | null;
  headDivisionName: string | null;
  poFile: POFile;
  freshdeskTicketId: string | null;
  skucount: number;
  status: string;
  subTotal: string;
  subTotalWithTax: string;
  subTotalWithTaxAmount: string;
  tags: Tag[];
  total: string;
  totalQuantity: number;
  totalWithTax: string;
  userType: string;
}

export interface OrderInvoice {
  totalQty: number;
  totalAmount: string;
  invoicedItem: number;
  invoiceLines: any[];
}

export interface OrderMetaData {
  id: string;
  refOrderNumber: string;
  erpOrderNumber: string | null;
}

export interface POFile {
  id: string;
  poNumber: string;
  importSource: string;
  isMapped: boolean;
  isparsed: boolean;
}

export interface Tag {
  id: number;
  name: string;
}

export interface CancelOrderRequest {
  customerId: string;
  orderId: number;
  workspaceId: string;
  sellerWorkspaceId: string;
}

export interface UpdatePOStatusRequest {
  sellerWorkspaceId: string;
  customerId: string;
  poFileId: string;
  status: string;
}

export interface AcceptOrderRequest {
  orderId?: number;
  status: string;
}

export type OrderRootState = ReturnType<typeof store.getState>;

export interface OrderDataLine {
  price: number;
  newQty: number;
  quantity: number;
  title?: string;
  packSize?: string | number;
  productVariantId: number;
  orderId?: number;
  lineDiscount?: {
    discount: string;
    discountType: DiscountType;
  };
}

export interface OrderDataSummary {
  total: number;
  discount: number;
  totalWithTax: number;
  tax: number;
  lines: OrderDataLine[];
  shippingLines: ShippingLines[];
}

export interface OrderFilterRequest {
  workspaceId: string;
  customerId: string;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
  includeSummary?: boolean;
  includeInvoice?: boolean;
  includeCustomer?: boolean;
  includeStatus?: boolean;
  includeCFA?: boolean;
  includeTeamCustomersMapping?: boolean;
  includeDivision?: boolean;
  searchKeyword?: string;
  startDate: string;
  endDate: string;
  filterModel?: {
    divisionIds?: string[];
    headDivisionIds?: string[];
    cfaIds?: string[];
    status?: string[];
    customerIds?: string[];
    importSource?: string;
    tagIds?: number[];
  };
  skip?: number;
}

export interface OrderFilterResponse {
  status: {
    status: string;
    statusCount: number;
  }[];
  divisions: {
    divisionId: string;
    divisionName: string;
  }[];
  fulfillments: {
    cfaIds: string;
    cfaName: string;
  }[];
  tags: {
    tagId: string;
    tagName: string;
  }[];
  headDivisions: {
    headDivisionId: string;
    headDivisionName: string;
  };
  customers: {
    customerId: string;
    customerName: string;
  }[];
  sourceFilter: {
    [key: string]: string;
  };
  userType: {
    [key: string]: string;
  };
}

export interface OrderSearchParameter {
  parameterId: string;
  value: string;
  updateSearchValue?: string | string[];
}

export interface ShippingAddress {
  fullName?: string;
  company?: string;
  streetLine1?: string;
  streetLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  countryCode?: string;
  phoneNumber?: string;
  latitude?: number;
  longitude?: number;
}

export type CheckoutNotes = Record<string, any>;

export interface AddressParam {
  latitude: number;
  longitude: number;
  address: string;
  name: string;
  url: string;
}

export interface JsonSchema {
  type: string;
  required?: string[];
  format?: string;
  properties?: Record<string, JsonSchema>;
  maxLength?: number;
  minLength?: number;
}

export interface FieldDefinition {
  required: boolean;
  type: string;
  format?: string;
  constraints?: {
    maxLength?: number;
    minLength?: number;
  };
}

export type SchemaFields = Record<string, FieldDefinition>;

export interface CreateOrderNoteRequest {
  sellerWorkspaceId: string;
  params: {
    sellerWorkspaceId: string;
  };
  body: JsonSchema;
}

export type Cart = Record<string, CustomerCart>;

export interface CustomerCart {
  customerName: string;
  companyName: string;
  phone?: string;
  customerCode?: string;
  poFileId?: string;
  orderAmount?: number;
  orderId?: number;
  tax?: number;
  orderIds?: number[];
  lines: ActiveOrderLine[];
  askForConfirmation?: boolean;
}

export type UOM = {
  uom: string;
  type: 'base' | 'additional';
  packSize: number;
};

export interface OrderPricelist {
  pricelistId: string;
  pricelistName: string;
}

export enum PromotionType {
  BUY_X_GET_Y = `buy_x_get_y_free`,
  PRODUCTS_PERCENTAGE_DISCOUNT = 'products_percentage_discount',
  PRODUCT_DISCOUNT = 'product_discount',
}

export type SchemeIconType = 'FREE' | 'PERCENT' | 'PRODUCTS' | 'DIVISION';

export interface OrderLevelScheme {
  promotionCode?: any;
  id: number;
  name: string;
  code: string;
  description: string;
  schemeType: string;
  minOrderQuantity?: number;
  promotionType?: PromotionType;
  schemeIconType?: SchemeIconType;
  productVariantId?: number;
}

export interface GetOrderLevelSchemesRequest {
  sellerWorkspaceId: string;
  orderId: number;
  customerId?: string;
}

export type GetOrderLevelSchemesResponse = {
  promotions: OrderLevelScheme[];
  totalRecords: number;
};

export interface LedgerSummaryReportsRequest {
  inviteIds: string[];
  sellerWorkspaceId: string;
  pageNo: number;
  pageSize: number;
}

export interface Records {
  sellerworkspaceid: string;
  inviteId: string;
  outstandingAmt: string;
  creditLimit: string;
  overdueAmt: string;
  availableLimit: string;
  createdAt: string;
  updatedAt: string;
}

export interface LedgerSummaryReportsResponse {
  startRecord: number;
  endRecord: number;
  totalRecords: number;
  data: Records[];
}
